import { takeLeading, takeEvery, select, put, all } from 'redux-saga/effects'
import {
  VALIDATE_PAR_FILE_SUCCESS,
  DOWNLOAD_PAR_FILE_FAILURE,
  downloadParFile,
} from 'actions/truck'
import {
  createSystemNotification,
  deleteSystemNotification,
} from 'actions/application'
import { ecuParFileDataSelector } from 'selectors/truck'
import strings from 'utils/strings'
import { NOTIFICATION_ERROR } from 'utils/constants'
import {
  PAR_FILE_DOWNLOAD_NOTIFY,
  PAR_FILE_ERROR_NOTIFY,
} from 'utils/notifications'

export default function* watchStartParFileDownload() {
  yield all([
    takeLeading(VALIDATE_PAR_FILE_SUCCESS, downloadParFileWorkflow),
    // Show an error notification when the download request fails.
    // The success notification is handled by persistentNotification.js
    // (DOWNLOAD_PAR_FILE_SUCCESS → downloadParFileSuccessNotification), so we
    // only need to handle the failure path here — defaultErrorHandler silently
    // swallows GET failures, which would otherwise leave the user with no
    // feedback when the .PAR download itself fails.
    takeEvery(DOWNLOAD_PAR_FILE_FAILURE, onDownloadFailure),
  ])
}

/**
 * Named exports for unit testing — not part of the public module API.
 * Import from test files only.
 */
export { downloadParFileWorkflow as downloadParFileWorkflowForTesting }
export { onDownloadFailure as onDownloadFailureForTesting }

function* downloadParFileWorkflow(action) {
  const { serialNumber, isPreProdRequest = false } = action
  const validationData = yield select(ecuParFileDataSelector, { serialNumber })

  yield put(deleteSystemNotification({ notificationId: PAR_FILE_ERROR_NOTIFY }))
  yield put(
    deleteSystemNotification({ notificationId: PAR_FILE_DOWNLOAD_NOTIFY })
  )
  // A clean legacy validation response downloads immediately. Error responses
  // are handled by the component modal, which can offer a manual partial
  // download only when an installed module remains error-free. Do NOT show a success
  // notification here — the download is dispatched fire-and-forget via yield put
  // and may fail silently (defaultErrorHandler swallows GET failures). The
  // success notification is handled by persistentNotification.js which listens
  // for DOWNLOAD_PAR_FILE_SUCCESS and fires only after the request completes.
  if (validationData?.isClean) {
    yield put(downloadParFile(serialNumber, isPreProdRequest))
  }
  // When hasParErrors is true, the component's promiseApiAction resolve callback
  // already opens the PAR error modal (wired via validateParFile resolve param).
  // The old saga-driven callback() call was removed when callback was moved out
  // of args — calling it here would invoke undefined() and crash the saga.
}

function* onDownloadFailure(action) {
  // defaultErrorHandler treats GET failures as silent (console.warn only).
  // Without this handler, a failed .PAR download would leave the user with
  // no feedback at all — the success notification from persistentNotification.js
  // would not fire (since DOWNLOAD_PAR_FILE_SUCCESS was never dispatched), but
  // no error would appear either. Show the existing error string so the user
  // knows to try again.
  //
  // EXCEPTION: when the download was triggered from inside an open modal
  // (isModalTriggered === true), the failure is already shown inline via
  // createModalErrorHandler(onError) — showing a global notification on top
  // would duplicate the feedback and violate the "in-modal errors stay in-modal"
  // contract. Skip the notification in that case.
  if (action.isModalTriggered) return

  yield put(
    deleteSystemNotification({ notificationId: PAR_FILE_DOWNLOAD_NOTIFY })
  )
  yield put(
    createSystemNotification({
      notificationMessage: strings.par_error_download_fail,
      notificationType: NOTIFICATION_ERROR,
      persistent: true,
      notificationId: PAR_FILE_ERROR_NOTIFY,
      closable: true,
      showIcon: false,
      icon: 'alert_banner_error_diamond_cross',
    })
  )
}
